/**
 * @group Services
 * 
 * @summary
 * <blockquote>HTTP service implementation for OpenEMR EHR API.</blockquote>
 * 
 * Provides base URL resolution and bearer token authentication headers
 * for OpenEMR API requests. Used for deployment health checks, vitals
 * validation, and any direct OpenEMR HTTP communication.
 * 
 * @example
 * ```typescript
 * import { OpenEMRHttpService, FetchApiClient } from '@shared/httpService';
 * 
 * const service = new OpenEMRHttpService({
 *   baseUrl: 'https://openemr.ehr.phila.life',
 *   accessToken: 'bearer-token-from-oauth',
 * });
 * const client = new FetchApiClient({
 *   service: { isLocal: false, service },
 * });
 * 
 * const vitals = await client.get('/apis/default/api/patient/1/vital');
 * ```
 */

import { ok, err, Result } from '@shared/utils';
import type { AppError } from '@shared/utils';
import type { IHttpService } from '../interfaces/IHttpService';

/**
 * @group Interfaces
 * @internal
 */
interface OpenEMRHttpServiceConfig {
  baseUrl: string;
  accessToken?: string;
}

/**
 * @internal
 */
export class OpenEMRHttpService implements IHttpService {
  private readonly config: OpenEMRHttpServiceConfig;

  constructor(config: OpenEMRHttpServiceConfig) {
    this.config = config;
  }

  getBaseUrl(): Result<string, AppError> {
    if (!this.config.baseUrl) {
      return err({
        errorCode: 'OPENEMR_BASE_URL_MISSING',
        errorMessage: 'OpenEMR base URL is required',
      });
    }

    return ok(this.config.baseUrl);
  }

  getHeaders(): Result<Record<string, string>, AppError> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.config.accessToken) {
      headers['Authorization'] = `Bearer ${this.config.accessToken}`;
    }

    return ok(headers);
  }

  getName(): string {
    return 'openemr';
  }
}
